import React, { useState, useEffect } from 'react';
import { BetConfig, BetConfigFormData } from '../api/betConfigsApi';

interface BetConfigFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (configData: BetConfigFormData) => Promise<void>;
  config: BetConfig;
  title: string;
}

const BetConfigFormModal: React.FC<BetConfigFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  config,
  title
}) => {
  const [formState, setFormState] = useState<BetConfigFormData>({
    round: '',
    matchday: null,
    name: '',
    description: '',
    general: false,
    points: 0,
    view_schema: {
      data: 'teams',
      size: 1
    }
  });
  
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Set form data when editing a configuration
  useEffect(() => {
    if (config) {
      setFormState({
        round: config.round || '',
        matchday: config.matchday,
        name: config.name || '',
        description: config.description || '',
        general: config.general || false,
        points: config.points || 0,
        view_schema: config.view_schema || {
          data: 'teams',
          size: 1
        }
      });
    }
  }, [config, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormState({
        ...formState,
        [name]: target.checked,
      });
    } else if (name === 'points') {
      setFormState({
        ...formState,
        [name]: parseInt(value, 10) || 0,
      });
    } else if (name === 'matchday') {
      setFormState({
        ...formState,
        [name]: value === '' ? null : parseInt(value, 10),
      });
    } else {
      setFormState({
        ...formState,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(formState);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-dark-100 rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="px-6 py-4 border-b border-dark-200">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-500 text-red-200 rounded">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">Round</label>
            <select
              name="round"
              value={formState.round}
              onChange={handleChange}
              className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              required
            >
              <option value="">Select a round</option>
              <option value="GROUP">Group Stage</option>
              <option value="ROUND_OF_32">Round of 32</option>
              <option value="ROUND_OF_16">Round of 16</option>
              <option value="QUARTER_FINAL">Quarter-Final</option>
              <option value="SEMI_FINAL">Semi-Final</option>
              <option value="THIRD_PLACE">Third Place</option>
              <option value="FINAL">Final</option>
              <option value="TOURNAMENT">Tournament</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">Matchday</label>
            <input
              type="number"
              name="matchday"
              value={formState.matchday || ''}
              onChange={handleChange}
              min="1"
              max="3"
              className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
            />
            <p className="mt-1 text-sm text-gray-400">Leave empty for non-group stage rounds</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={formState.name}
              onChange={handleChange}
              className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea
              name="description"
              value={formState.description}
              onChange={handleChange}
              rows={3}
              className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="general"
                checked={formState.general}
                onChange={handleChange}
                className="h-4 w-4 text-stake-blue focus:ring-stake-blue border-dark-300 rounded bg-dark-200"
              />
              <span className="mr-2 text-sm text-gray-300">להראות למשתמשים</span>
            </label>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">Points</label>
            <input
              type="number"
              name="points"
              value={formState.points}
              onChange={handleChange}
              min="1"
              className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              required
            />
          </div>

          {/* View Schema Configuration (only for general bets) */}
          {formState.general && (
            <div className="space-y-4 border-t border-dark-200 pt-4 mt-4">
              <h3 className="font-medium text-white">UI Configuration</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Data Type
                  </label>
                  <select
                    className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                    value={formState.view_schema?.data || 'teams'}
                    onChange={(e) => setFormState({
                      ...formState,
                      view_schema: {
                        data: e.target.value as 'teams' | 'players' | 'groups' | 'number',
                        size: formState.view_schema?.size || 1
                      }
                    })}
                  >
                    <option value="teams">Teams</option>
                    <option value="players">Players</option>
                    <option value="groups">Groups</option>
                    <option value="number">Number</option>
                  </select>
                  <p className="text-sm text-gray-400 mt-1">
                    What type of data users will select
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Selection Size
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                    value={formState.view_schema?.size || 1}
                    min={1}
                    onChange={(e) => setFormState({
                      ...formState,
                      view_schema: {
                        data: formState.view_schema?.data || 'teams',
                        size: parseInt(e.target.value) || 1
                      }
                    })}
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    How many items users need to select
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 border-t border-dark-200 pt-4">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-md shadow-sm hover:bg-dark-300 focus:outline-none focus:ring-2 focus:ring-stake-blue focus:ring-offset-2 focus:ring-offset-dark-100"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-stake-blue border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-stake-blue focus:ring-offset-2 focus:ring-offset-dark-100"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BetConfigFormModal; 